import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Eye, EyeOff, ChevronDown, Check, UserCheck, Key } from 'lucide-react';
import axios from 'axios';

const Signup = () => {
  const navigate = useNavigate();
  const [isPreRegistered, setIsPreRegistered] = useState(false);
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [preRegisteredData, setPreRegisteredData] = useState(null);
  const [checkingAdmission, setCheckingAdmission] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    courseId: '',
    campusId: ''
  });
  const [courses, setCourses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState({ course: false, campus: false });
  const [institutionName, setInstitutionName] = useState('Institution');
  const [learningMode, setLearningMode] = useState('instructor_led');
  const [institutionId, setInstitutionId] = useState(null);

  useEffect(() => {
    loadInstitutionData();
    fetchCoursesAndCampuses();
  }, []);

  const loadInstitutionData = () => {
    try {
      const selectedInstitutionString = localStorage.getItem('selectedInstitution');
      if (selectedInstitutionString) {
        const selectedInstitution = JSON.parse(selectedInstitutionString);
        setInstitutionName(selectedInstitution.name || 'Institution');
        setLearningMode(selectedInstitution.learningMode || 'instructor_led');
        setInstitutionId(selectedInstitution.id);
      }
    } catch (error) {
      console.error('Error loading institution data:', error);
    }
  };

  const fetchCoursesAndCampuses = async () => {
    setFetchingData(true);
    try {
      // Get selected institution from localStorage
      const selectedInstitutionString = localStorage.getItem('selectedInstitution');
      let institutionId = null;

      if (selectedInstitutionString) {
        const selectedInstitution = JSON.parse(selectedInstitutionString);
        institutionId = selectedInstitution.id;
      }

      // Build URLs with institutionId parameter
      const coursesUrl = institutionId
        ? `/courses/public/all?institutionId=${institutionId}`
        : '/courses/public/all';

      const campusesUrl = institutionId
        ? `/campuses/public/all?institutionId=${institutionId}`
        : '/campuses/public/all';

      const [coursesResponse, campusesResponse] = await Promise.all([
        axios.get(coursesUrl),
        axios.get(campusesUrl)
      ]);

      setCourses(coursesResponse.data);
      setCampuses(campusesResponse.data);

      // Set default values
      if (coursesResponse.data.length > 0) {
        setFormData(prev => ({ ...prev, courseId: coursesResponse.data[0].id.toString() }));
      }
      if (campusesResponse.data.length > 0) {
        setFormData(prev => ({ ...prev, campusId: campusesResponse.data[0].id.toString() }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load courses and campuses.');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleAdmissionCheck = async () => {
    if (!admissionNumber.trim()) {
      setError('Please enter your admission number.');
      return;
    }

    setCheckingAdmission(true);
    setError('');

    try {
      const response = await axios.get(`/auth/student-by-admission/${encodeURIComponent(admissionNumber.trim())}`);

      if (response.data) {
        setPreRegisteredData(response.data);
        setFormData({
          name: response.data.name,
          email: response.data.email,
          password: '',
          confirmPassword: '',
          phoneNumber: response.data.phoneNumber,
          courseId: response.data.courseId.toString(),
          campusId: response.data.campusId.toString()
        });
      }
    } catch (error) {
      console.error('Admission check error:', error);
      if (error.response?.status === 404) {
        setError('Admission number not found. Please check and try again.');
      } else if (error.response?.status === 400) {
        setError('Account already activated. Please login instead.');
      } else {
        setError('An error occurred while checking admission number. Please try again.');
      }
    } finally {
      setCheckingAdmission(false);
    }
  };

  const handlePreRegisteredSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.password || !formData.confirmPassword) {
      setError('Please enter and confirm your password.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/register/pre-registered', {
        admissionNumber: admissionNumber.trim(),
        password: formData.password,
      });

      if (response.data) {
        alert('Account activated successfully! Please login.');
        navigate('/login');
      }
    } catch (error) {
      console.error('Pre-registered signup error:', error);
      setError(error.response?.data?.message || 'An error occurred during account activation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    const isSelfPaced = learningMode === 'self_paced';

    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword ||
      !formData.phoneNumber || (!isSelfPaced && (!formData.courseId || !formData.campusId))) {
      setError('Please fill all fields.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/register/student', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phoneNumber: formData.phoneNumber,
        courseId: formData.courseId ? parseInt(formData.courseId) : null,
        campusId: formData.campusId ? parseInt(formData.campusId) : null,
        institutionId: institutionId,
      });

      if (response.data) {
        alert('Account created successfully! Please login.');
        navigate('/login');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.response?.data?.message || 'An error occurred during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetPreRegisteredFlow = () => {
    setIsPreRegistered(false);
    setAdmissionNumber('');
    setPreRegisteredData(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      courseId: courses.length > 0 ? courses[0].id.toString() : '',
      campusId: campuses.length > 0 ? campuses[0].id.toString() : ''
    });
    setError('');
  };

  const getSelectedCourseName = () => {
    const course = courses.find(c => c.id.toString() === formData.courseId);
    return course ? course.name : 'Select a course...';
  };

  const getSelectedCampusName = () => {
    const campus = campuses.find(c => c.id.toString() === formData.campusId);
    return campus ? campus.name : 'Select a campus...';
  };

  if (fetchingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-full flex items-center justify-center mb-4">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
            Create Account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Join {institutionName} and start your learning journey
          </p>
        </div>

        {/* Signup Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-200">
          {/* Signup Type Toggle */}
          <div className="mb-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => resetPreRegisteredFlow()}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${!isPreRegistered
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <UserCheck className="w-4 h-4" />
                  <span>New Student</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setIsPreRegistered(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${isPreRegistered
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Key className="w-4 h-4" />
                  <span>Pre-registered</span>
                </div>
              </button>
            </div>
          </div>

          {isPreRegistered ? (
            // Pre-registered Student Flow
            <div className="space-y-6">
              {!preRegisteredData ? (
                // Admission Number Input
                <div>
                  <label htmlFor="admissionNumber" className="form-label">
                    Admission Number
                  </label>
                  <div className="flex space-x-3">
                    <input
                      id="admissionNumber"
                      type="text"
                      className="flex-1 form-input"
                      placeholder="Enter your admission number"
                      value={admissionNumber}
                      onChange={(e) => setAdmissionNumber(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAdmissionCheck();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAdmissionCheck}
                      disabled={checkingAdmission}
                      className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {checkingAdmission ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        'Check'
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the admission number provided by your institution
                  </p>
                </div>
              ) : (
                // Password Setup for Pre-registered Student
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-green-800 mb-2">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Student Details Found</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p><strong>Name:</strong> {preRegisteredData.name}</p>
                      <p><strong>Email:</strong> {preRegisteredData.email}</p>
                      <p><strong>Course:</strong> {preRegisteredData.course?.name || 'N/A'}</p>
                      <p><strong>Campus:</strong> {preRegisteredData.campus?.name || 'N/A'}</p>
                    </div>
                  </div>

                  <form onSubmit={handlePreRegisteredSubmit} className="space-y-6">
                    {/* Password Field */}
                    <div>
                      <label htmlFor="password" className="form-label">
                        Create Password
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          required
                          className="form-input pr-10"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          required
                          className="form-input pr-10"
                          placeholder="Confirm your password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Activating Account...
                        </div>
                      ) : (
                        'Activate Account'
                      )}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={resetPreRegisteredFlow}
                    className="w-full py-2 px-4 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    ← Back to admission number entry
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Regular Signup Form
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="form-label">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              {/* Phone Number Field */}
              <div>
                <label htmlFor="phoneNumber" className="form-label">
                  Phone Number
                </label>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  autoComplete="tel"
                  required
                  className="form-input"
                  placeholder="Enter your phone number"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="form-input pr-10"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="form-input pr-10"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Course Selection (Instructor-Led Only or Optional for Self-Paced if desired, currently hidden per request) */}
              {learningMode !== 'self_paced' && (
                <>
                  <div>
                    <label className="form-label">
                      Select Course
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(prev => ({ ...prev, course: !prev.course }))}
                        className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <span className={formData.courseId ? 'text-gray-900' : 'text-gray-500'}>
                          {getSelectedCourseName()}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen.course ? 'rotate-180' : ''}`} />
                      </button>

                      {dropdownOpen.course && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {courses.map((course) => (
                            <button
                              key={course.id}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, courseId: course.id.toString() }));
                                setDropdownOpen(prev => ({ ...prev, course: false }));
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                            >
                              <span className="text-gray-900">{course.name}</span>
                              {formData.courseId === course.id.toString() && (
                                <Check className="w-5 h-5 text-green-600 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Campus Selection */}
                  <div>
                    <label className="form-label">
                      Select Campus
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(prev => ({ ...prev, campus: !prev.campus }))}
                        className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <span className={formData.campusId ? 'text-gray-900' : 'text-gray-500'}>
                          {getSelectedCampusName()}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen.campus ? 'rotate-180' : ''}`} />
                      </button>

                      {dropdownOpen.campus && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {campuses.map((campus) => (
                            <button
                              key={campus.id}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, campusId: campus.id.toString() }));
                                setDropdownOpen(prev => ({ ...prev, campus: false }));
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                            >
                              <span className="text-gray-900">{campus.name}</span>
                              {formData.campusId === campus.id.toString() && (
                                <Check className="w-5 h-5 text-green-600 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          )}

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 {institutionName} Learning Management System. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
